import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  ScrollView,
} from 'react-native';
import { Button, Card, ActivityIndicator } from 'react-native-paper';
import { userService } from '../../services/userService';
import { theme } from '../../constants/theme';

type InvitationData = Awaited<ReturnType<typeof userService.validateInvitation>>;
type InvitationNavigation = {
  navigate: (screen: 'Login') => void;
};

interface InvitationRegisterScreenProps {
  route?: {
    params: {
      token: string;
    };
  };
  navigation?: InvitationNavigation;
  token?: string; // For direct token passing
}

const InvitationRegisterScreen: React.FC<InvitationRegisterScreenProps> = ({ 
  route, 
  navigation,
  token: directToken 
}) => {
  // Get token from route params or direct prop (for web)
  const token = directToken || route?.params?.token;
  
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [invitationData, setInvitationData] = useState<InvitationData | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (token) {
      validateInvitation();
    }
  }, [token]);

  const validateInvitation = async () => {
    try {
      setLoading(true);
      const data = await userService.validateInvitation(token!);
      setInvitationData(data);
    } catch (error) {
      console.error('Error validating invitation:', error);
      alert('This invitation link is invalid or has expired.');
      if (navigation) {
        navigation.navigate('Login');
      } else {
        window.location.href = '/login';
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!firstName.trim() || !lastName.trim() || !password.trim()) {
      alert('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }

    try {
      setRegistering(true);
      await userService.registerWithInvitation(
        token!,
        password,
        firstName.trim(),
        lastName.trim()
      );

      alert('Welcome! Your account has been created successfully. Please log in with your new credentials.');

      if (navigation) {
        navigation.navigate('Login');
      } else {
        window.location.href = '/login';
      }
    } catch (error) {
      console.error('Error registering with invitation:', error);
      alert('Failed to create account. Please try again.');
    } finally {
      setRegistering(false);
    }
  };

  const handleBackToLogin = () => {
    if (navigation) {
      navigation.navigate('Login');
    } else {
      window.location.href = '/login';
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>Validating invitation...</Text>
      </View>
    );
  }

  if (!invitationData) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Invalid or expired invitation</Text>
        <Button
          mode="contained"
          onPress={handleBackToLogin}
          style={styles.backButton}
        >
          Back to Login
        </Button>
      </View>
    );
  }

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <Card style={styles.card}>
        <Card.Content style={styles.cardContent}>
          <Text style={styles.title}>Create Your Account</Text>
          <Text style={styles.subtitle}>
            You've been invited to join the Exhale Home Review system
          </Text>

          <View style={styles.invitationInfo}>
            <Text style={styles.invitationLabel}>Email:</Text>
            <Text style={styles.invitationValue}>{invitationData.email}</Text>
            {invitationData.inviter && (
              <>
                <Text style={styles.invitationLabel}>Invited by:</Text>
                <Text style={styles.invitationValue}>
                  {invitationData.inviter.firstName} {invitationData.inviter.lastName}
                </Text>
              </>
            )}
          </View>

          <View style={styles.form}>
            <TextInput
              style={styles.input}
              placeholder="First Name"
              value={firstName}
              onChangeText={setFirstName}
              autoCapitalize="words"
            />

            <TextInput
              style={styles.input}
              placeholder="Last Name"
              value={lastName}
              onChangeText={setLastName}
              autoCapitalize="words"
            />

            <TextInput
              style={styles.input}
              placeholder="Password (minimum 6 characters)"
              value={password}
              onChangeText={setPassword}
              secureTextEntry
            />

            <TextInput
              style={styles.input}
              placeholder="Confirm Password"
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              secureTextEntry
            />

            <Button
              mode="contained"
              onPress={handleRegister}
              loading={registering}
              disabled={registering}
              style={styles.registerButton}
            >
              Create Account
            </Button>

            <Button
              mode="text"
              onPress={handleBackToLogin}
              style={styles.backToLoginButton}
            >
              Back to Login
            </Button>
          </View>
        </Card.Content>
      </Card>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    justifyContent: 'center',
    padding: 20,
    backgroundColor: theme.colors.background,
    minHeight: '100%',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background,
    minHeight: '100%',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: theme.colors.onSurface,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: theme.colors.background,
    minHeight: '100%',
  },
  errorText: {
    fontSize: 18,
    color: theme.colors.error,
    textAlign: 'center',
    marginBottom: 24,
  },
  backButton: {
    marginTop: 16,
  },
  card: {
    maxWidth: 500,
    alignSelf: 'center',
    width: '100%',
  },
  cardContent: {
    padding: 32,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: theme.colors.onSurface,
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: theme.colors.onSurfaceVariant,
    textAlign: 'center',
    marginBottom: 32,
  },
  invitationInfo: {
    backgroundColor: theme.colors.surfaceVariant,
    padding: 20,
    borderRadius: 12,
    marginBottom: 32,
  },
  invitationLabel: {
    fontSize: 14,
    color: theme.colors.onSurfaceVariant,
    fontWeight: '600',
    marginBottom: 4,
  },
  invitationValue: {
    fontSize: 16,
    color: theme.colors.onSurface,
    marginBottom: 12,
  },
  form: {
    gap: 20,
  },
  input: {
    borderWidth: 1,
    borderColor: theme.colors.outline,
    borderRadius: 8,
    padding: 16,
    fontSize: 16,
    backgroundColor: theme.colors.background,
  },
  registerButton: {
    marginTop: 12,
    paddingVertical: 8,
  },
  backToLoginButton: {
    marginTop: 8,
  },
});

export default InvitationRegisterScreen;
